/**
 * FilterDropdowns - Dropdown panel for filter categories
 *
 * Core component: No internal dependencies.
 * Renders below toolbar, shows category pills and expanded filter boxes.
 * Use with FilterButton and useFilterPanel hook.
 */

import { useState, useEffect, useRef } from 'react';
import { X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  /** Sum value (only for 'sum' type categories) */
  sum?: number;
}

export type FilterCategoryType = 'multi' | 'single' | 'sum' | 'range';

export interface FilterCategory {
  id: string;
  label: string;
  /** Options (not used for 'range' type) */
  options?: FilterOption[];
  /** Category type: 'multi' (toggle multiple), 'single' (pick one), 'sum' (show Σ + value), 'range' (min/max inputs) */
  type?: FilterCategoryType;
  /** Suffix for sum/range values (e.g., "₼") */
  suffix?: string;
  /** Placeholder for min input (range type only) */
  minPlaceholder?: string;
  /** Placeholder for max input (range type only) */
  maxPlaceholder?: string;
}

export interface ActiveFilters {
  [categoryId: string]: string[];
}

// 10 glassy color presets
const glassyColors = [
  { border: 'border-sky-300', text: 'text-sky-600' },
  { border: 'border-rose-300', text: 'text-rose-600' },
  { border: 'border-amber-300', text: 'text-amber-600' },
  { border: 'border-emerald-300', text: 'text-emerald-600' },
  { border: 'border-violet-300', text: 'text-violet-600' },
  { border: 'border-orange-300', text: 'text-orange-600' },
  { border: 'border-teal-300', text: 'text-teal-600' },
  { border: 'border-pink-300', text: 'text-pink-600' },
  { border: 'border-indigo-300', text: 'text-indigo-600' },
  { border: 'border-cyan-300', text: 'text-cyan-600' },
];

const getGlassyColor = (index: number) => {
  const safeIndex = Math.abs(index) % glassyColors.length;
  return glassyColors[safeIndex]!;
};

const grayBoxBg = 'bg-slate-200/50';

export interface FilterDropdownsProps {
  categories: FilterCategory[];
  activeFilters: ActiveFilters;
  onFiltersChange: (filters: ActiveFilters) => void;
  isCategoryBoxOpen: boolean;
  onCategoryBoxClose: () => void;
  expandedCategories: string[];
  onExpandCategory: (id: string) => void;
  onCollapseCategory: (id: string) => void;
}

/** Helper: Parse range values from activeFilters array */
function parseRangeValues(values: string[]): { min: string; max: string } {
  let min = '';
  let max = '';
  for (const v of values) {
    if (v.startsWith('min:')) min = v.slice(4);
    if (v.startsWith('max:')) max = v.slice(4);
  }
  return { min, max };
}

/** Helper: Build range values array for activeFilters */
function buildRangeValues(min: string, max: string): string[] {
  const result: string[] = [];
  if (min) result.push(`min:${min}`);
  if (max) result.push(`max:${max}`);
  return result;
}

/** Range inputs component for 'range' type categories */
function RangeInputs({
  categoryId,
  category,
  color,
  activeFilters,
  onFiltersChange,
}: {
  categoryId: string;
  category: FilterCategory;
  color: { border: string; text: string };
  activeFilters: ActiveFilters;
  onFiltersChange: (filters: ActiveFilters) => void;
}) {
  const currentValues = activeFilters[categoryId] || [];
  const { min, max } = parseRangeValues(currentValues);

  const handleChange = (type: 'min' | 'max', value: string) => {
    // Only allow numeric input
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;

    const newMin = type === 'min' ? value : min;
    const newMax = type === 'max' ? value : max;
    onFiltersChange({
      ...activeFilters,
      [categoryId]: buildRangeValues(newMin, newMax),
    });
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        inputMode="decimal"
        value={min}
        onChange={(e) => handleChange('min', e.target.value)}
        placeholder={category.minPlaceholder || 'Min'}
        className={cn(
          'w-20 px-3 py-2.5 rounded-full bg-white text-center',
          'border',
          color.border,
          'text-sm font-medium',
          color.text,
          'placeholder:text-slate-400',
          'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current',
          'transition-all'
        )}
      />
      <span className={cn('text-sm', color.text)}>—</span>
      <input
        type="text"
        inputMode="decimal"
        value={max}
        onChange={(e) => handleChange('max', e.target.value)}
        placeholder={category.maxPlaceholder || 'Max'}
        className={cn(
          'w-20 px-3 py-2.5 rounded-full bg-white text-center',
          'border',
          color.border,
          'text-sm font-medium',
          color.text,
          'placeholder:text-slate-400',
          'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current',
          'transition-all'
        )}
      />
      {category.suffix && (
        <span className={cn('text-sm font-medium', color.text)}>
          {category.suffix}
        </span>
      )}
    </div>
  );
}

export function FilterDropdowns({
  categories,
  activeFilters,
  onFiltersChange,
  isCategoryBoxOpen,
  onCategoryBoxClose,
  expandedCategories,
  onExpandCategory,
  onCollapseCategory,
}: FilterDropdownsProps) {
  const availableCategories = categories.filter(
    (cat) => !expandedCategories.includes(cat.id)
  );

  const getCategoryIndex = (categoryId: string) =>
    categories.findIndex((c) => c.id === categoryId);

  const handleCategorySelect = (categoryId: string) => {
    onExpandCategory(categoryId);
    onCategoryBoxClose();
    if (!activeFilters[categoryId]) {
      onFiltersChange({ ...activeFilters, [categoryId]: [] });
    }
  };

  const handleOptionToggle = (categoryId: string, value: string) => {
    const category = categories.find((c) => c.id === categoryId);
    const categoryType = category?.type || 'multi';
    const currentSelection = activeFilters[categoryId] || [];
    const isSelected = currentSelection.includes(value);

    let newSelection: string[];

    if (categoryType === 'single' || categoryType === 'sum') {
      // Single select: toggle off if already selected, otherwise select this one only
      newSelection = isSelected ? [] : [value];
    } else {
      // Multi select: toggle on/off
      newSelection = isSelected
        ? currentSelection.filter((v) => v !== value)
        : [...currentSelection, value];
    }

    onFiltersChange({ ...activeFilters, [categoryId]: newSelection });
  };

  const handleDismissCategory = (categoryId: string) => {
    onCollapseCategory(categoryId);
    const newFilters = { ...activeFilters };
    delete newFilters[categoryId];
    onFiltersChange(newFilters);
  };

  const hasContent = isCategoryBoxOpen || expandedCategories.length > 0;
  if (!hasContent) return null;

  return (
    <div className="space-y-2 mt-3">
      {/* Category selection box */}
      {isCategoryBoxOpen && availableCategories.length > 0 && (
        <div className={cn('p-3 rounded-2xl', grayBoxBg)}>
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((category) => {
              const colorIndex = getCategoryIndex(category.id);
              const color = getGlassyColor(colorIndex);
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategorySelect(category.id)}
                  className={cn(
                    'px-4 py-2.5 rounded-full',
                    'bg-transparent border',
                    color.border,
                    color.text,
                    'text-sm font-medium',
                    'hover:bg-white/40 active:bg-white/60 transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current'
                  )}
                >
                  {category.type === 'sum' && <span className="mr-1">Σ</span>}
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Expanded filter boxes */}
      {expandedCategories.map((categoryId) => {
        const category = categories.find((c) => c.id === categoryId);
        if (!category) return null;

        const colorIndex = getCategoryIndex(categoryId);
        const color = getGlassyColor(colorIndex);
        const selectedValues = activeFilters[categoryId] || [];

        return (
          <div
            key={categoryId}
            className={cn(
              'p-3 rounded-2xl border',
              grayBoxBg,
              color.border
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className={cn('text-sm font-semibold', color.text)}>
                {category.label}
              </span>
              <button
                type="button"
                onClick={() => handleDismissCategory(categoryId)}
                className={cn(
                  'p-1.5 rounded-full',
                  color.text,
                  'opacity-50 hover:opacity-100 hover:bg-white/50',
                  'transition-all',
                  'focus:outline-none focus:ring-2 focus:ring-current'
                )}
                aria-label={`Clear ${category.label} filter`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Options / Range inputs */}
            {category.type === 'range' ? (
              <RangeInputs
                categoryId={categoryId}
                category={category}
                color={color}
                activeFilters={activeFilters}
                onFiltersChange={onFiltersChange}
              />
            ) : (
              <div className={cn(
                category.type === 'sum' ? 'flex flex-col gap-1' : 'flex flex-wrap gap-2'
              )}>
                {(category.options || []).map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  const isSumType = category.type === 'sum';

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionToggle(categoryId, option.value)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 rounded-full',
                        'text-sm font-medium transition-all',
                        'border',
                        color.border,
                        color.text,
                        'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current',
                        isSelected
                          ? 'bg-white/70 shadow-sm'
                          : 'bg-transparent hover:bg-white/40',
                        isSumType && 'justify-between rounded-xl px-4 py-3'
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                        {isSumType && <span className="font-semibold">Σ</span>}
                        {option.label}
                      </span>
                      {isSumType && option.sum !== undefined && (
                        <span className="font-semibold">
                          {option.sum.toLocaleString()}{category.suffix ? ` ${category.suffix}` : ''}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * useFilterPanel - Hook for filter state management
 */
export function useFilterPanel(_categories: FilterCategory[]) {
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
  const [isCategoryBoxOpen, setIsCategoryBoxOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isCategoryBoxOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsCategoryBoxOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryBoxOpen]);

  const toggleCategoryBox = () => setIsCategoryBoxOpen(!isCategoryBoxOpen);
  const closeCategoryBox = () => setIsCategoryBoxOpen(false);
  const expandCategory = (id: string) => setExpandedCategories([...expandedCategories, id]);
  const collapseCategory = (id: string) => setExpandedCategories(expandedCategories.filter((c) => c !== id));

  // Count total active filter selections
  const activeFilterCount = Object.values(activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  return {
    activeFilters,
    setActiveFilters,
    isCategoryBoxOpen,
    toggleCategoryBox,
    closeCategoryBox,
    expandedCategories,
    expandCategory,
    collapseCategory,
    panelRef,
    hasActiveFilters: expandedCategories.length > 0,
    activeFilterCount,
  };
}
